import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  captureLead,
  createCheckoutSession,
  fetchPrices,
  trackLeadServerEvent,
  trackPageViewServerEvent,
  trackViewContentServerEvent,
  type StripePricesResponse,
} from "./api";
import { setCheckoutCompletionCookie } from "./checkoutCookie";
import {
  captureFunnelScreenPageview,
  firePageViewBrowser,
  getTrackingContext,
  initAnalytics,
  registerFunnelPersonProperties,
  rememberPendingCheckoutTracking,
  track,
  trackMetaInitiateCheckout,
  trackMetaLead,
  trackMetaViewContent,
  upgradeExternalIdToLead,
  waitForFbp,
} from "./analytics";
import { brand, config } from "./config";
import { checkoutAfterPaymentSteps, checkoutPlans, getRecommendedPlan, getResultInsights, moneyBackGuaranteeLabel, paymentExplainerSteps, paymentFaqs, questions, resultBullets, socialProof, testimonials, valueChips, type Answers, type PlanId } from "./funnelData";
import { BrandLogo, Icon } from "./icons";
import { getCheckoutOfferFromLocation, isPolarTrialOffer, type CheckoutOffer } from "./checkoutOffer";
import {
  DEFAULT_FUNNEL_CONCEPT,
  getFunnelConceptFromLocation,
  getStoredOrDefaultFunnelConcept,
  type FunnelConcept,
} from "./funnelConcept";
import {
  EMAIL_STEP,
  getFunnelScreenName,
  getHashForScreen,
  getPaymentStep,
  parseHashToScreen,
  RESULT_STEP,
  WELCOME_STEP,
} from "./funnelRoutes";

type StoredFunnelState = {
  step?: number;
  email?: string;
  answers?: Answers;
  selectedPlan?: PlanId;
  leadId?: number | null;
  entryConceptKey?: string;
};

type CheckoutTimelineStep = {
  title: string;
  subtitle: string;
  icon?: "bell";
};

const STORAGE_KEY = "awakeo:funnel:v2";
const CHECKOUT_TOKEN_KEY = "awakeo:checkout-completion-token";
const META_VIEW_CONTENT_SESSION_KEY = "awakeo:meta-view-content-fired";
const META_LEAD_SESSION_PREFIX = "awakeo:meta-lead-fired:";
const META_PAGE_VIEW_SESSION_KEY = "awakeo:meta-page-view-fired";
const EMAIL_STEP_QUERY_PARAM = "collect_email";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_MEDIA_QUERY = "(max-width: 760px)";
// Below this width the sticky desktop conversion sidebar is hidden and the fixed
// bottom checkout bar (same email + CTA) takes over. Must match the `.side-proof`
// hide breakpoint in styles.css.
const PAYMENT_FLOATING_CTA_MEDIA_QUERY = "(max-width: 860px)";
const LAUNCH_OFFER_DURATION_MS = 15 * 60 * 1000;
const CHECKOUT_DEADLINE_KEY = "awakeo:checkout-deadline";
const emailFrictionHandlers = ["No spam", "Instant download", "Activation details only"] as const;

const formatCountdown = (remainingMs: number): string => {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const getInitialEntryConcept = (): FunnelConcept => {
  if (typeof window === "undefined") return DEFAULT_FUNNEL_CONCEPT;
  // A live ?concept= in the URL always wins (matches the ad creative the user just clicked).
  const fromUrl = getFunnelConceptFromLocation(window.location);
  if (fromUrl) return fromUrl;
  // Otherwise keep the ad-matched title from a resumed session.
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const storedKey = raw ? (JSON.parse(raw) as StoredFunnelState).entryConceptKey : null;
    return getStoredOrDefaultFunnelConcept(storedKey);
  } catch {
    return DEFAULT_FUNNEL_CONCEPT;
  }
};

const money = (unitAmount: number, currency: string): string =>
  `${currency.toUpperCase()} ${new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: unitAmount % 100 === 0 ? 0 : 2,
  }).format(unitAmount / 100)}`;

const annualMoney = (price: { unitAmount: number; currency: string } | undefined, fallback: string): string =>
  price ? `${money(price.unitAmount, price.currency)}/year` : fallback;

const normalizeEmail = (value: string): string => value.trim().toLowerCase();
const isValidEmail = (value: string): boolean => EMAIL_REGEX.test(value);

const parseBooleanQueryFlag = (location: Location, queryParam: string, fallback: boolean): boolean => {
  const raw = location.search ? new URLSearchParams(location.search).get(queryParam)?.trim().toLowerCase() : null;
  if (raw == null || raw === "") return fallback;
  if (raw === "1" || raw === "true" || raw === "yes" || raw === "on") return true;
  if (raw === "0" || raw === "false" || raw === "no" || raw === "off") return false;
  return fallback;
};

// The dedicated email step is on by default so every ad click-through captures the lead
// before checkout. Append `?collect_email=0` to any funnel URL to fall back to the inline
// email-at-payment flow (useful for A/B or debugging).
const shouldShowEmailStepFromLocation = (location: Location): boolean =>
  parseBooleanQueryFlag(location, EMAIL_STEP_QUERY_PARAM, true);

const findCheckoutPlan = (planId: PlanId) =>
  checkoutPlans.find((plan) => plan.id === planId) ?? checkoutPlans[0];

const getInitialCheckoutOffer = (): CheckoutOffer => {
  if (typeof window === "undefined") return getCheckoutOfferFromLocation({ search: "" } as Location);
  return getCheckoutOfferFromLocation(window.location);
};

function TrustBadge({ className }: { className?: string }) {
  return (
    <div
      className={className ? `trust-badge ${className}` : "trust-badge"}
      aria-label={`Rated ${socialProof.rating} out of ${socialProof.ratingMax}, trusted by ${socialProof.audience}`}
    >
      <div className="trust-badge__avatars" aria-hidden="true">
        {socialProof.avatars.map((avatar) => (
          <img key={avatar} src={avatar} alt="" loading="lazy" decoding="async" width={32} height={32} />
        ))}
      </div>
      <div className="trust-badge__copy">
        <span className="trust-badge__stars" aria-hidden="true">
          ★★★★★ <strong>{socialProof.rating}/{socialProof.ratingMax}</strong>
        </span>
        <span className="trust-badge__label">Trusted by {socialProof.audience}</span>
      </div>
    </div>
  );
}

export default function App() {
  const [entryConcept] = useState<FunnelConcept>(() => getInitialEntryConcept());
  const [checkoutOffer] = useState<CheckoutOffer>(() => getInitialCheckoutOffer());
  const [showDedicatedEmailStep] = useState<boolean>(() =>
    typeof window !== "undefined" ? shouldShowEmailStepFromLocation(window.location) : false,
  );
  const [step, setStep] = useState(WELCOME_STEP);
  const [answers, setAnswers] = useState<Answers>({});
  const [email, setEmail] = useState("");
  const [leadId, setLeadId] = useState<number | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanId>("pro");
  const [prices, setPrices] = useState<StripePricesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedMobilePlanId, setExpandedMobilePlanId] = useState<PlanId>("pro");
  const [isMobile, setIsMobile] = useState(false);
  const [showFloatingCheckoutCta, setShowFloatingCheckoutCta] = useState(false);
  const [offerRemainingMs, setOfferRemainingMs] = useState(LAUNCH_OFFER_DURATION_MS);
  const lastCapturedLeadEmailRef = useRef<string | null>(null);
  const emailCaptureInFlightRef = useRef<string | null>(null);

  const routeOptions = useMemo(() => ({ includeEmailStep: showDedicatedEmailStep }), [showDedicatedEmailStep]);
  const paymentStep = getPaymentStep(showDedicatedEmailStep);
  const showWelcome = step === WELCOME_STEP;
  const currentQuestion = step >= 1 && step <= questions.length ? questions[step - 1] : undefined;
  const showQuestion = Boolean(currentQuestion);
  const showResult = step === RESULT_STEP;
  const showEmail = showDedicatedEmailStep && step === EMAIL_STEP;
  const showPayment = step >= paymentStep;
  const progress = Math.min(100, Math.round((step / paymentStep) * 100));
  const recommendedPlan = useMemo(() => getRecommendedPlan(answers), [answers]);
  const resultInsights = useMemo(() => getResultInsights(answers), [answers]);
  const pageUrl = typeof window === "undefined" ? undefined : window.location.href;
  const normalizedEmail = normalizeEmail(email);
  const hasValidCheckoutEmail = isValidEmail(normalizedEmail);
  const canGoBack = step > WELCOME_STEP;
  const isTrialCheckoutOffer = isPolarTrialOffer(checkoutOffer);
  const checkoutProviderName = checkoutOffer.provider === "polar" ? "Polar" : "Stripe";
  const offerAnalyticsKey = checkoutOffer.analyticsKey;

  const normalizeStepForFlow = useCallback(
    (candidateStep: number): number => {
      const nextStep = Number.isFinite(candidateStep) ? Math.max(WELCOME_STEP, candidateStep) : WELCOME_STEP;
      if (showDedicatedEmailStep) return nextStep;
      return nextStep >= EMAIL_STEP ? paymentStep : nextStep;
    },
    [paymentStep, showDedicatedEmailStep],
  );

  useEffect(() => {
    initAnalytics();
    track("funnel_loaded", {
      entry_concept: entryConcept.key,
      entry_concept_label: entryConcept.label,
      checkout_offer: offerAnalyticsKey,
    });
  }, [entryConcept.key, entryConcept.label, offerAnalyticsKey]);

  useEffect(() => {
    if (typeof sessionStorage === "undefined") return;
    if (sessionStorage.getItem(META_PAGE_VIEW_SESSION_KEY) === "1") return;
    sessionStorage.setItem(META_PAGE_VIEW_SESSION_KEY, "1");
    const tracking = getTrackingContext();
    const pageViewEventId = `pv-${tracking.eventId}`;
    firePageViewBrowser(pageViewEventId);
    if (!config.apiBaseUrl) return;
    void waitForFbp().then(() => {
      const fresh = getTrackingContext();
      void trackPageViewServerEvent({
        eventId: pageViewEventId,
        fbp: fresh.fbp,
        fbc: fresh.fbc,
        externalId: fresh.externalId,
        pageUrl,
      }).catch((e) => console.warn("[meta] PageView CAPI failed", e));
    });
  }, [pageUrl]);

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as StoredFunnelState;
        setStep(typeof parsed.step === "number" ? normalizeStepForFlow(parsed.step) : WELCOME_STEP);
        setEmail(parsed.email ?? "");
        setAnswers(parsed.answers ?? {});
        setLeadId(parsed.leadId ?? null);
        if (parsed.selectedPlan === "standard" || parsed.selectedPlan === "pro" || parsed.selectedPlan === "unlimited") {
          if (isTrialCheckoutOffer && parsed.selectedPlan === "unlimited") {
            setSelectedPlan("pro");
          } else {
            setSelectedPlan(parsed.selectedPlan);
          }
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }

    const hashScreen = parseHashToScreen(window.location.hash, routeOptions);
    if (hashScreen) setStep(normalizeStepForFlow(hashScreen.step));
  }, [isTrialCheckoutOffer, normalizeStepForFlow, routeOptions]);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ step, email, answers, selectedPlan, leadId, entryConceptKey: entryConcept.key }),
    );
  }, [step, email, answers, selectedPlan, leadId, entryConcept.key]);

  useEffect(() => {
    if (!config.apiBaseUrl) return;
    fetchPrices(isTrialCheckoutOffer ? checkoutOffer.id : undefined).then(setPrices).catch(() => {});
  }, [checkoutOffer.id, isTrialCheckoutOffer]);

  // Keep the mobile-expanded plan in sync with the chosen plan.
  useEffect(() => {
    setExpandedMobilePlanId(selectedPlan);
  }, [selectedPlan]);

  // Track the mobile breakpoint so the floating CTA + collapsed cards only apply on small screens.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const media = window.matchMedia(MOBILE_MEDIA_QUERY);
    const sync = () => setIsMobile(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  // Launch-offer countdown — tied to the real discount, persisted per session so it
  // doesn't reset every time the user navigates a step.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let deadline = Number(sessionStorage.getItem(CHECKOUT_DEADLINE_KEY));
    if (!Number.isFinite(deadline) || deadline <= 0) {
      deadline = Date.now() + LAUNCH_OFFER_DURATION_MS;
      sessionStorage.setItem(CHECKOUT_DEADLINE_KEY, String(deadline));
    }
    const tick = () => setOfferRemainingMs(Math.max(0, deadline - Date.now()));
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, []);

  // On the payment step, small screens get a fixed bottom checkout bar (email + CTA);
  // the sticky desktop sidebar carries the same controls above the breakpoint.
  useEffect(() => {
    if (!showPayment) {
      setShowFloatingCheckoutCta(false);
      return;
    }
    if (typeof window === "undefined" || !window.matchMedia) return;
    const media = window.matchMedia(PAYMENT_FLOATING_CTA_MEDIA_QUERY);
    const sync = () => setShowFloatingCheckoutCta(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, [showPayment]);

  useEffect(() => {
    registerFunnelPersonProperties({
      recommended_plan: recommendedPlan,
      entry_concept: entryConcept.key,
      entry_concept_label: entryConcept.label,
      checkout_offer: offerAnalyticsKey,
    });
  }, [recommendedPlan, entryConcept.key, entryConcept.label, offerAnalyticsKey]);

  useEffect(() => {
    captureFunnelScreenPageview(
      step,
      { recommendedPlan, entryConceptKey: entryConcept.key, entryConceptLabel: entryConcept.label },
      routeOptions,
    );
  }, [step, recommendedPlan, entryConcept.key, entryConcept.label, routeOptions]);

  useEffect(() => {
    track("funnel_step_viewed", {
      step,
      screen_name: getFunnelScreenName(step, routeOptions),
      recommended_plan: recommendedPlan,
      entry_concept: entryConcept.key,
      checkout_offer: offerAnalyticsKey,
    });
  }, [step, recommendedPlan, entryConcept.key, routeOptions, offerAnalyticsKey]);

  useEffect(() => {
    const nextHash = getHashForScreen(step, routeOptions);
    if (window.location.hash !== nextHash) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${nextHash}`);
    }
  }, [step, routeOptions]);

  const fireViewContent = useCallback(() => {
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(META_VIEW_CONTENT_SESSION_KEY)) return;
    // Guard synchronously so awaiting _fbp below can't let a second call slip through.
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(META_VIEW_CONTENT_SESSION_KEY, "1");
    const tracking = getTrackingContext();
    const plan = recommendedPlan;
    const live = prices?.[plan];
    const staticPlan = findCheckoutPlan(plan);
    const value = isTrialCheckoutOffer
      ? checkoutOffer.priceAmount
      : live ? live.unitAmount / 100 : Number(staticPlan.price.replace(/[^0-9.]/g, "") ?? 0);
    const currency = (isTrialCheckoutOffer ? checkoutOffer.currency : live?.currency ?? "USD").toUpperCase();
    const contentName = `Awakeo ${staticPlan.name}`;
    const viewContentEventId = `vc-${tracking.eventId}`;
    trackMetaViewContent({ eventId: viewContentEventId, contentIds: [plan], value, currency, contentName });
    // Wait for the _fbp cookie so the server event carries it (browser + server dedup on eventId).
    void waitForFbp()
      .then(() => {
        const fresh = getTrackingContext();
        return trackViewContentServerEvent({
          eventId: viewContentEventId,
          contentIds: [plan],
          value,
          currency,
          contentName,
          fbp: fresh.fbp,
          fbc: fresh.fbc,
          externalId: fresh.externalId,
          pageUrl,
        });
      })
      .catch((e) => console.warn("[meta] ViewContent CAPI failed", e));
  }, [checkoutOffer.currency, checkoutOffer.priceAmount, isTrialCheckoutOffer, pageUrl, prices, recommendedPlan]);

  const advanceFromWelcome = useCallback(() => {
    setStep(1);
  }, []);

  const selectAnswer = useCallback((questionId: string, optionId: string) => {
    setAnswers((current) => ({ ...current, [questionId]: optionId }));
    track("funnel_answer_selected", {
      question_id: questionId,
      option_id: optionId,
      entry_concept: entryConcept.key,
    });
    window.setTimeout(() => {
      setStep((current) => {
        if (current <= questions.length) return current + 1;
        return current;
      });
    }, 160);
  }, [entryConcept.key]);

  useEffect(() => {
    if (step !== RESULT_STEP) return;
    setSelectedPlan(recommendedPlan);
    fireViewContent();
  }, [step, recommendedPlan, fireViewContent]);

  const advanceFromResult = useCallback(() => {
    setError("");
    setStep(showDedicatedEmailStep ? EMAIL_STEP : paymentStep);
  }, [paymentStep, showDedicatedEmailStep]);

  const captureEmailLead = useCallback(
    (rawEmail: string): boolean => {
      const nextEmail = normalizeEmail(rawEmail);
      if (!config.enableEmailCapture || !isValidEmail(nextEmail)) return false;
      if (!config.apiBaseUrl) return false;
      if (lastCapturedLeadEmailRef.current === nextEmail || emailCaptureInFlightRef.current === nextEmail) {
        return true;
      }

      emailCaptureInFlightRef.current = nextEmail;
      const leadTracking = getTrackingContext();
      const leadEventId = `lead-${leadTracking.eventId}`;
      const leadMetaSessionKey = `${META_LEAD_SESSION_PREFIX}${nextEmail}`;
      const shouldTrackLeadMetaEvent =
        typeof sessionStorage === "undefined" || sessionStorage.getItem(leadMetaSessionKey) !== "1";

      void (async () => {
        try {
          const [leadSaveResult, leadTrackResult] = await Promise.allSettled([
            captureLead({
              email: nextEmail,
              onboardingResponses: { concept: entryConcept.key, ...answers },
              source: "awakeo_webfunnel",
            }),
            shouldTrackLeadMetaEvent
              ? trackLeadServerEvent({
                  email: nextEmail,
                  eventId: leadEventId,
                  fbp: leadTracking.fbp,
                  fbc: leadTracking.fbc,
                  externalId: leadTracking.externalId,
                  pageUrl,
                })
              : Promise.resolve(),
          ]);

          if (leadSaveResult.status === "fulfilled") {
            lastCapturedLeadEmailRef.current = nextEmail;
            setLeadId(leadSaveResult.value.leadId);
            if (typeof leadSaveResult.value.leadId === "number") {
              upgradeExternalIdToLead(leadSaveResult.value.leadId);
            }
            track("email_captured", { lead_id: leadSaveResult.value.leadId, recommended_plan: recommendedPlan });
          }

          if (shouldTrackLeadMetaEvent) {
            if (leadTrackResult.status === "rejected") console.warn("[leads] CAPI lead failed", leadTrackResult.reason);
            trackMetaLead(leadEventId, { email: nextEmail });
            sessionStorage.setItem(leadMetaSessionKey, "1");
          }
        } finally {
          if (emailCaptureInFlightRef.current === nextEmail) emailCaptureInFlightRef.current = null;
        }
      })();

      return true;
    },
    [answers, entryConcept.key, pageUrl, recommendedPlan],
  );

  const saveEmailAndContinue = () => {
    setError("");
    if (!hasValidCheckoutEmail) {
      setError("Enter a valid email for download and activation details.");
      return;
    }
    captureEmailLead(normalizedEmail);
    setStep(paymentStep);
  };

  const startCheckout = async (planId: PlanId) => {
    setError("");
    setSelectedPlan(planId);
    if (!config.apiBaseUrl) {
      setError("Checkout API is not configured. Set VITE_API_BASE_URL to your awakeo-checkout-api worker URL.");
      return;
    }

    try {
      setIsLoading(true);
      const staticPlan = findCheckoutPlan(planId);
      const price = prices?.[planId];
      const tracking = getTrackingContext();
      if (hasValidCheckoutEmail) captureEmailLead(normalizedEmail);
      const value = isTrialCheckoutOffer
        ? checkoutOffer.priceAmount
        : price ? price.unitAmount / 100 : Number(staticPlan.price.replace(/[^0-9.]/g, "") ?? 0);
      const currency = isTrialCheckoutOffer ? checkoutOffer.currency : price?.currency ?? "USD";
      track("checkout_started", { plan_id: planId, value, currency, checkout_offer: offerAnalyticsKey });
      trackMetaInitiateCheckout(tracking.eventId, planId, value, currency);

      const response = await createCheckoutSession({
        email: hasValidCheckoutEmail ? normalizedEmail : undefined,
        leadId: leadId ?? undefined,
        planId,
        flow: "a",
        offer: isTrialCheckoutOffer ? checkoutOffer.id : undefined,
        value,
        eventId: tracking.eventId,
        fbp: tracking.fbp ?? undefined,
        fbc: tracking.fbc ?? undefined,
        externalId: tracking.externalId,
        pageUrl,
      });

      track(isTrialCheckoutOffer ? "checkout_redirect_to_polar" : "checkout_redirect_to_stripe", {
        plan_id: planId,
        checkout_session_id: response.sessionId,
        checkout_offer: offerAnalyticsKey,
        value,
        currency,
      });

      sessionStorage.setItem(CHECKOUT_TOKEN_KEY, response.completionToken);
      setCheckoutCompletionCookie(response.completionToken);
      rememberPendingCheckoutTracking({
        sessionId: response.sessionId,
        eventId: tracking.eventId,
        planId,
        value,
        currency,
        completionToken: response.completionToken,
      });
      window.location.assign(response.checkoutUrl);
    } catch (checkoutError) {
      track("checkout_failed", {
        plan_id: planId,
        checkout_offer: offerAnalyticsKey,
        message: checkoutError instanceof Error ? checkoutError.message : "Checkout failed",
      });
      setError(checkoutError instanceof Error ? checkoutError.message : "Checkout could not be started.");
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    setError("");
    setStep((current) => Math.max(WELCOME_STEP, current - 1));
  };

  const selectedPlanData = findCheckoutPlan(selectedPlan);
  const selectedLivePrice = prices?.[selectedPlan];
  const selectedRenewalPrice = annualMoney(
    selectedLivePrice,
    checkoutOffer.renewalDisplay[selectedPlan] ?? selectedPlanData.price,
  );
  const selectedPlanPrice = isTrialCheckoutOffer
    ? checkoutOffer.priceDisplay
    : selectedLivePrice
      ? money(selectedLivePrice.unitAmount, selectedLivePrice.currency)
      : selectedPlanData.price;
  const selectedPlanPriceDetail = isTrialCheckoutOffer
    ? `${selectedPlanPrice} for ${checkoutOffer.trialDays} days, then ${selectedRenewalPrice}`
    : `${selectedPlanPrice} one-time`;
  const offerActive = !isTrialCheckoutOffer && offerRemainingMs > 0;
  const checkoutCtaLabel = isLoading
    ? "Redirecting…"
    : isTrialCheckoutOffer
      ? `Start ${checkoutOffer.trialDays}-day free trial`
      : `Checkout · ${selectedPlanPrice}`;
  const emailStepTitle = isTrialCheckoutOffer
    ? `Unlock your ${checkoutOffer.trialDays}-day free trial`
    : "Where should we send activation details?";
  const emailStepBody =
    "We'll send your download link and activation details here. Use an inbox you actually check.";
  const emailStepCta = isTrialCheckoutOffer
    ? `Unlock my ${checkoutOffer.trialDays}-day free trial`
    : "Continue to checkout";
  const paymentEyebrow = isTrialCheckoutOffer ? `${checkoutOffer.trialDays}-day trial` : "One-time purchase";
  const paymentTitle = isTrialCheckoutOffer ? "Start free. Keep agents running." : "Pay once. Keep agents running.";
  const paymentBody = isTrialCheckoutOffer
    ? `Try Awakeo free for ${checkoutOffer.trialDays} days. Standard renews at ${annualMoney(prices?.standard, checkoutOffer.renewalDisplay.standard ?? "A$19/year")} and Pro renews at ${annualMoney(prices?.pro, checkoutOffer.renewalDisplay.pro ?? "A$29/year")} unless canceled.`
    : "Local-first macOS utility with lifetime updates included.";
  const activeValueChips = isTrialCheckoutOffer
    ? ["Free trial", "Native macOS", "Cancel anytime", "Battery-aware"]
    : valueChips;
  const activeTestimonials = isTrialCheckoutOffer
    ? testimonials.filter((testimonial) => !testimonial.quote.toLowerCase().includes("no subscription"))
    : testimonials;
  const activePaymentFaqs = isTrialCheckoutOffer
    ? [
        {
          question: "What happens after the free trial?",
          answer: `You can try Awakeo free for ${checkoutOffer.trialDays} days. Standard renews at ${annualMoney(prices?.standard, checkoutOffer.renewalDisplay.standard ?? "A$19/year")} and Pro renews at ${annualMoney(prices?.pro, checkoutOffer.renewalDisplay.pro ?? "A$29/year")} unless canceled.`,
        },
        ...paymentFaqs.filter(
          (faq) =>
            faq.question !== "Is this a subscription?" &&
            !faq.question.toLowerCase().includes("one-time") &&
            !faq.question.toLowerCase().includes("one time"),
        ),
      ]
    : paymentFaqs;
  const checkoutTimelineSteps: readonly CheckoutTimelineStep[] = isTrialCheckoutOffer
    ? [
        {
          title: "Start your free trial",
          subtitle: "Use the same email for checkout and activation.",
        },
        {
          title: "Download instantly and activate",
          subtitle: "Activate with the inbox you used at checkout.",
        },
        {
          title: "Get a reminder before renewal",
          subtitle: "We'll email you 3 days before the trial ends. You can manage or cancel before renewal.",
          icon: "bell" as const,
        },
        {
          title: "Keep using Awakeo after the trial",
          subtitle: checkoutOffer.renewalCopy,
        },
      ]
    : checkoutAfterPaymentSteps;

  return (
    <div className="page-shell page-shell--funnel">
      <header className="funnel-header">
        {canGoBack ? (
          <button className="top-back" type="button" onClick={goBack} aria-label="Go back">
            ←
          </button>
        ) : (
          <div className="top-back-spacer" aria-hidden="true" />
        )}
        <a className="brand-lockup" href="https://awakeo.app" aria-label="Awakeo home">
          <img className="brand-lockup__icon" src={brand.navbarIconPath} width={40} height={40} alt="" />
          <span className="brand-lockup__wordmark">Awakeo</span>
        </a>
        <span className="header-note">Native · Calm · Reliable</span>
      </header>

      <main className={`funnel-main ${showPayment ? "funnel-main--payment" : ""}`}>
        <section className={`funnel-card reveal ${showPayment ? "" : "funnel-card--compact"}`}>
          <div className="progress-track" aria-hidden="true">
            <span style={{ width: `${progress}%` }} />
          </div>

          {showWelcome ? (
            <div className="hero-step">
              <div className="hero-step__layout">
                <div className="hero-step__copy">
                  <div className="hero-chip">Built for macOS</div>
                  <h1>
                    <span className="hero-title-main">{entryConcept.heroTitleMain}</span>
                    {entryConcept.heroTitleAccent ? (
                      <span className="hero-title-accent">{entryConcept.heroTitleAccent}</span>
                    ) : null}
                  </h1>
                  <p className="hero-body">{entryConcept.heroSubtitle}</p>
                </div>
                <div className="hero-step__visual hero-visual--float" aria-hidden="true">
                  <div className="hero-product">
                    <img
                      className="hero-product__image"
                      src={brand.heroGraphicPath}
                      alt=""
                      width={1448}
                      height={1086}
                      loading="eager"
                      decoding="async"
                    />
                  </div>
                </div>
                <div className="hero-step__actions">
                  <div className="hero-actions">
                    <button
                      className="button button-primary button-hero"
                      type="button"
                      onClick={advanceFromWelcome}
                    >
                      Get started
                    </button>
                  </div>
                  <p className="welcome-cta-friction">
                    {isTrialCheckoutOffer
                      ? `${checkoutOffer.trialDays}-day trial · Local-first · Cancel anytime`
                      : "One-time purchase · Local-first · No subscription"}
                  </p>
                  <TrustBadge className="trust-badge--hero" />
                  <div className="trust-row">
                    <span className="trust-row__chip">
                      <Icon name="shield" />
                      Local first
                    </span>
                    <span className="trust-row__chip">
                      <Icon name="battery" />
                      Battery aware
                    </span>
                    <span className="trust-row__chip trust-row__chip--agents">
                      <span className="agent-logos" aria-hidden="true">
                        <BrandLogo name="claude" />
                        <BrandLogo name="openai" />
                        <BrandLogo name="cursor" />
                      </span>
                      Built for agents
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {showQuestion && currentQuestion ? (
            <div className="question-step">
              <p className="section-kicker">{currentQuestion.eyebrow}</p>
              <h2>{currentQuestion.title}</h2>
              <p className="hero-body">{currentQuestion.subtitle}</p>
              <div className="option-grid">
                {currentQuestion.options.map((option) => (
                  <button
                    className={`option-card tone-${option.tone}`}
                    type="button"
                    key={option.id}
                    onClick={() => selectAnswer(currentQuestion.id, option.id)}
                  >
                    <span className="option-dot">
                      <Icon name={option.icon} />
                    </span>
                    <strong>{option.label}</strong>
                    <small>{option.description}</small>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {showResult ? (
            <div className="result-step">
              <span className="result-eyebrow">Your continuity estimate</span>

              <div className="result-metric-card">
                <div className="result-metric">
                  <strong className="result-metric__value">~{resultInsights.exposedHoursPerWeek}</strong>
                  <span className="result-metric__unit">hrs/week</span>
                </div>
                <p className="result-metric__label">
                  of agent runtime exposed to {resultInsights.riskShort}
                </p>
                <p className="result-metric__caption">{resultInsights.exposureCaption}</p>
              </div>

              <p className="result-solution">
                <Icon name="shield" />
                <span>{resultInsights.headline}</span>
              </p>

              <div className="result-summary" aria-label="Your answers">
                <div className="result-summary-row tone-violet">
                  <span className="result-summary-row__icon">
                    <Icon name={resultInsights.riskIcon} />
                  </span>
                  <span className="result-summary-row__label">Biggest risk</span>
                  <strong className="result-summary-row__value">{resultInsights.sessionBreak}</strong>
                </div>
                <div className="result-summary-row tone-blue">
                  <span className="result-summary-row__icon">
                    <Icon name="clock" />
                  </span>
                  <span className="result-summary-row__label">Typical run</span>
                  <strong className="result-summary-row__value">{resultInsights.typicalRun}</strong>
                </div>
                <div className="result-summary-row tone-green">
                  <span className="result-summary-row__icon">
                    <Icon name="desktop" />
                  </span>
                  <span className="result-summary-row__label">Your setup</span>
                  <strong className="result-summary-row__value">{resultInsights.macSetup}</strong>
                </div>
              </div>

              <div className="result-status">
                <span className="status-lights" aria-hidden="true">
                  <i className="status-light status-light--go" />
                  <i className="status-light status-light--guard" />
                  <i className="status-light status-light--off" />
                </span>
                <div className="result-status__copy">
                  <strong>Status at a glance</strong>
                  <span>See which agents are working, finished, or awaiting instructions, all in one menu bar view.</span>
                </div>
              </div>

              <div className="result-panel">
                <div className="result-panel__visual">
                  <img
                    src={brand.menubarPreviewPath}
                    alt=""
                    width={1254}
                    height={1254}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="result-panel__copy">
                  <span className="result-panel__badge">Recommended</span>
                  <h3>
                    {resultInsights.planName} · {resultInsights.planDetail}
                  </h3>
                  <p className="hero-body">
                    {recommendedPlan === "standard"
                      ? isTrialCheckoutOffer
                        ? "Try Awakeo on one Mac with battery guardrails built in."
                        : "One Mac, full Awakeo feature set with battery guardrails built in."
                      : isTrialCheckoutOffer
                        ? "Try Awakeo across work and personal machines before the annual plan begins."
                        : "Best value for developers running agents across work and personal machines."}
                  </p>
                  <ul className="result-bullets">
                    {resultBullets.map((bullet) => (
                      <li key={bullet}>
                        <Icon name="check" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <TrustBadge />

              <div className="sticky-cta">
                <button className="button button-primary button-hero" type="button" onClick={advanceFromResult}>
                  Continue
                </button>
              </div>
            </div>
          ) : null}

          {showEmail ? (
            <div className="email-step">
              <div className="section-intro section-intro--narrow">
                <p className="section-kicker">One last thing</p>
                <h2>{emailStepTitle}</h2>
                <p className="hero-body">{emailStepBody}</p>
              </div>
              <label className="email-field">
                <input
                  type="email"
                  value={email}
                  placeholder="you@example.com"
                  autoComplete="email"
                  onChange={(event) => setEmail(event.target.value)}
                />
              </label>
              {error ? <p className="error-text">{error}</p> : null}
              <button className="button button-primary button-hero" type="button" disabled={isLoading} onClick={saveEmailAndContinue}>
                {isLoading ? "Saving…" : emailStepCta}
              </button>
              <div className="email-friction-handlers" aria-label="Email reassurance">
                {emailFrictionHandlers.map((item) => (
                  <span key={item}>
                    <Icon name="check" />
                    {item}
                  </span>
                ))}
              </div>
              <TrustBadge className="trust-badge--email" />
              <p className="welcome-cta-friction">
                By continuing you agree to Awakeo&apos;s{" "}
                <a href={brand.termsUrl} target="_blank" rel="noreferrer">
                  Terms
                </a>{" "}
                and{" "}
                <a href={brand.privacyPolicyUrl} target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          ) : null}

          {showPayment ? (
            <div className="payment-step" id="pricing">
              {offerActive ? (
                <div className="launch-offer" role="status">
                  <span className="launch-offer__label">Launch pricing</span>
                  <span className="launch-offer__detail">
                    Save while it lasts · ends in <strong>{formatCountdown(offerRemainingMs)}</strong>
                  </span>
                </div>
              ) : null}
              <div className="payment-step__hero">
                <div className="section-intro section-intro--narrow">
                  <p className="section-kicker">{paymentEyebrow}</p>
                  <h2>{paymentTitle}</h2>
                  <p className="hero-body">{paymentBody}</p>
                  <div className="value-chips value-chips--marquee" aria-label="Awakeo highlights">
                    <div className="value-chips__track">
                      {[...activeValueChips, ...activeValueChips].map((chip, index) => (
                        <span key={`${chip}-${index}`} aria-hidden={index >= activeValueChips.length}>
                          {chip}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="payment-step__visual" aria-hidden="true">
                  <div className="hero-product">
                    <img
                      className="hero-product__image hero-product__image--payment"
                      src={brand.heroGraphicPath}
                      alt=""
                      width={1448}
                      height={1086}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
              </div>
              <div className="plan-grid">
                {checkoutPlans.map((plan) => {
                  const livePrice = prices?.[plan.id];
                  const renewalPrice = annualMoney(livePrice, checkoutOffer.renewalDisplay[plan.id] ?? plan.price);
                  const displayPrice = isTrialCheckoutOffer
                    ? checkoutOffer.priceDisplay
                    : livePrice ? money(livePrice.unitAmount, livePrice.currency) : plan.price;
                  const priceTerm = isTrialCheckoutOffer ? `for ${checkoutOffer.trialDays} days` : "one-time";
                  const planFeatures = isTrialCheckoutOffer
                    ? [
                        "Free during trial",
                        `Then ${renewalPrice}`,
                        plan.id === "standard" ? "1 device" : "Up to 5 devices",
                        "Cancel anytime",
                      ]
                    : plan.features.slice(0, 4);
                  const isCollapsed = isMobile && expandedMobilePlanId !== plan.id;
                  return (
                    <article
                      className={`plan-card ${selectedPlan === plan.id ? "is-selected" : ""} ${plan.id === "pro" ? "is-featured" : ""} ${isCollapsed ? "plan-card--mobile-collapsed" : ""}`}
                      key={plan.id}
                      onClick={() => {
                        setSelectedPlan(plan.id);
                        setExpandedMobilePlanId(plan.id);
                      }}
                    >
                      {plan.badge ? <span className="plan-badge">{plan.badge}</span> : null}
                      <span className="plan-card__chevron" aria-hidden="true">›</span>
                      <p className="plan-usage">{plan.detail}</p>
                      <h3>{plan.name}</h3>
                      <div className="plan-price">
                        {isTrialCheckoutOffer ? null : <span className="plan-price__original">{plan.originalPrice}</span>}
                        <span className="plan-price__main">
                          <strong>{displayPrice}</strong>
                          <span className="plan-price__term">{priceTerm}</span>
                        </span>
                      </div>
                      <p className="plan-description">
                        {isTrialCheckoutOffer ? `Renews at ${renewalPrice} unless canceled.` : plan.description}
                      </p>
                      <ul>
                        {planFeatures.map((f) => (
                          <li key={f}>
                            <Icon name="check" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </article>
                  );
                })}
              </div>
              {error ? <p className="error-text">{error}</p> : null}
              <TrustBadge />
              <div className="pricing-trust">
                <span>
                  <Icon name="lock" />
                  {isTrialCheckoutOffer ? "Secure checkout" : `Secure checkout with ${checkoutProviderName}`}
                </span>
                <span>
                  <Icon name="download" />
                  Instant download after checkout
                </span>
                <span>
                  <Icon name="shield" />
                  {isTrialCheckoutOffer ? "Cancel anytime" : moneyBackGuaranteeLabel}
                </span>
              </div>
              <section className="checkout-next-steps" aria-label="What happens after payment">
                <div className="checkout-next-steps__copy">
                  <p className="section-kicker">After payment</p>
                  <h3>{isTrialCheckoutOffer ? "After trial checkout" : "The next 30 seconds are simple"}</h3>
                </div>
                <ol className="checkout-timeline">
                  {checkoutTimelineSteps.map((item, index) => (
                    <li key={item.title}>
                      <span className={item.icon ? "checkout-timeline__index checkout-timeline__index--icon" : "checkout-timeline__index"}>
                        {item.icon ? <Icon name={item.icon} /> : index + 1}
                      </span>
                      <p>
                        <strong>{item.title}</strong>
                        <span>{item.subtitle}</span>
                      </p>
                    </li>
                  ))}
                </ol>
              </section>
              <section className="payment-testimonials" aria-label="Customer reviews">
                <div className="payment-rating-proof">
                  <span className="payment-rating-proof__stars" aria-hidden="true">
                    ★★★★★
                  </span>
                  <p>
                    Rated {socialProof.rating}/{socialProof.ratingMax} by {socialProof.audience}
                  </p>
                </div>
                <div className="review-scroller">
                  {activeTestimonials.map((t) => (
                    <article className="review-card" key={t.author}>
                      <span className="review-card__stars" aria-hidden="true">
                        ★★★★★
                      </span>
                      <blockquote>&ldquo;{t.quote}&rdquo;</blockquote>
                      <div className="review-meta">
                        <img className="review-avatar" src={t.image} alt="" width={28} height={28} loading="lazy" />
                        <span className="review-meta__copy">
                          <strong>{t.author}</strong>
                          <span>{t.detail}</span>
                        </span>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
              <details className="product-highlight product-highlight--collapsible">
                <summary className="product-highlight__summary">
                  <span>
                    <span className="section-kicker">How Awakeo works</span>
                    <strong>See the menu bar workflow</strong>
                  </span>
                  <Icon name="chevron" className="product-highlight__chevron" />
                </summary>
                <div className="product-highlight__content">
                  <div className="payment-explainer" aria-label="How Awakeo works step by step">
                    {paymentExplainerSteps.map((stepItem) =>
                      stepItem.bonus ? (
                        <article
                          key={stepItem.title}
                          className="payment-explainer-card payment-explainer-card--key shimmer-frame"
                        >
                          <span className="payment-explainer-card__tag">Key feature</span>
                          <strong>{stepItem.title}</strong>
                          <p>{stepItem.description}</p>
                          <span className="payment-explainer-card__agents" aria-hidden="true">
                            <BrandLogo name="claude" />
                            <BrandLogo name="openai" />
                            <BrandLogo name="cursor" />
                          </span>
                        </article>
                      ) : (
                        <article key={stepItem.title} className={`payment-explainer-card tone-${stepItem.tone}`}>
                          <span className="option-dot" aria-hidden="true">
                            <Icon name={stepItem.icon} />
                          </span>
                          <strong>{stepItem.title}</strong>
                          <p>{stepItem.description}</p>
                        </article>
                      ),
                    )}
                  </div>
                </div>
              </details>
              <section className="agents-glance shimmer-frame" aria-label="Your agents at a glance">
                <div className="agents-glance__bar" aria-hidden="true">
                  <img className="agents-glance__icon" src={brand.appIconPath} alt="" width={26} height={26} loading="lazy" />
                  <span className="agents-glance__lights">
                    <i className="traffic-light traffic-light--red" />
                    <i className="traffic-light traffic-light--amber" />
                    <i className="traffic-light traffic-light--green" />
                  </span>
                </div>
                <div className="agents-glance__copy">
                  <span className="section-kicker">Your agents at a glance</span>
                  <strong>Traffic lights in your menu bar</strong>
                  <p>Green is running, amber needs you, red has stopped — every agent, one glance.</p>
                </div>
              </section>
              <section className="faq-block" aria-label="Frequently asked questions">
                <p className="section-kicker">FAQ</p>
                <h3 className="faq-block__title">Questions before checkout?</h3>
                <div className="faq-list">
                  {activePaymentFaqs.map((faq) => (
                    <details className="faq-item" key={faq.question}>
                      <summary>
                        <span>{faq.question}</span>
                        <Icon name="chevron" className="faq-item__chevron" />
                      </summary>
                      <p>{faq.answer}</p>
                    </details>
                  ))}
                </div>
              </section>
            </div>
          ) : null}
        </section>

        {showPayment ? (
          <aside className="side-proof" aria-label="Checkout">
            <div className="side-proof__cta">
              <div className="side-proof__heading">
                <img src={brand.appIconPath} alt="" width={42} height={42} loading="lazy" />
                <span className="side-proof__heading-copy">
                  <strong>Get Awakeo</strong>
                  <span>
                    {selectedPlanData.name} · {selectedPlanPriceDetail}
                  </span>
                </span>
              </div>
              {!showDedicatedEmailStep ? (
                <div className="payment-email-inline">
                  <label className="payment-email-inline__label" htmlFor="checkout-email-sidebar">
                    Used mainly for activation and product updates
                  </label>
                  <input
                    id="checkout-email-sidebar"
                    className="payment-email-inline__input"
                    type="email"
                    value={email}
                    placeholder="you@example.com"
                    inputMode="email"
                    autoComplete="email"
                    aria-label="Email for activation details"
                    onBlur={(event) => captureEmailLead(event.target.value)}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
              ) : null}
              <div
                className="checkout-card-rating"
                aria-label={`Rated ${socialProof.rating} out of ${socialProof.ratingMax}, trusted by ${socialProof.audience}`}
              >
                <span className="checkout-card-rating__stars" aria-hidden="true">
                  ★★★★★
                </span>
                <span>
                  {socialProof.rating}/{socialProof.ratingMax} · {socialProof.audience}
                </span>
              </div>
              <button
                className="button button-primary checkout-cta-button"
                type="button"
                disabled={isLoading}
                onClick={() => startCheckout(selectedPlan)}
              >
                {checkoutCtaLabel}
              </button>
              <p className="side-proof__cta-note">
                {isTrialCheckoutOffer
                  ? `Secure checkout · Free trial · ${selectedRenewalPrice} after trial`
                  : "Secure Stripe checkout · Pay once · Use forever"}
              </p>
              <p className="checkout-guarantee">
                <Icon name="shield" />
                <span>
                  <strong>{isTrialCheckoutOffer ? "Cancel anytime" : moneyBackGuaranteeLabel}</strong>
                  <small>{isTrialCheckoutOffer ? checkoutOffer.renewalCopy : "If Awakeo isn't a fit."}</small>
                </span>
              </p>
            </div>
            <div className="proof-metrics">
              <span>
                <Icon name="shield" />
                <strong>Local-first</strong>
              </span>
              <span>
                <Icon name="lock" />
                <strong>{isTrialCheckoutOffer ? "Secure checkout" : `${checkoutProviderName} secure`}</strong>
              </span>
              <span>
                <Icon name="download" />
                <strong>Instant access</strong>
              </span>
            </div>
          </aside>
        ) : null}
      </main>

      {showPayment && showFloatingCheckoutCta ? (
        <div className="floating-checkout-cta" role="region" aria-label="Checkout">
          {!showDedicatedEmailStep ? (
            <div className="payment-email-inline">
              <label className="payment-email-inline__label" htmlFor="checkout-email-floating">
                Used mainly for activation and product updates
              </label>
              <input
                id="checkout-email-floating"
                className="payment-email-inline__input"
                type="email"
                value={email}
                placeholder="you@example.com"
                inputMode="email"
                autoComplete="email"
                aria-label="Email for activation details"
                onBlur={(event) => captureEmailLead(event.target.value)}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
          ) : null}
          <button
            className="button button-primary checkout-cta-button"
            type="button"
            disabled={isLoading}
            onClick={() => startCheckout(selectedPlan)}
          >
            {checkoutCtaLabel}
          </button>
          <p className="floating-checkout-cta__trust">
            {isTrialCheckoutOffer
              ? `Free trial · ${selectedRenewalPrice} after trial · Secure checkout`
              : "Pay once · 15-day guarantee · Secure Stripe checkout"}
          </p>
        </div>
      ) : null}

      {showWelcome ? (
        <footer className="funnel-footer">
          <a href={brand.privacyPolicyUrl} target="_blank" rel="noreferrer">
            Privacy
          </a>
          <a href={brand.termsUrl} target="_blank" rel="noreferrer">
            Terms
          </a>
        </footer>
      ) : null}
    </div>
  );
}
